// src/hooks/sync/useAutoCheckIn.js
// MOVED: hooks/useAutoCheckIn.js → hooks/sync/useAutoCheckIn.js  (Phase 6)
// IMPORT FIXES:
//   '../utils/dateUtils'       → '@/utils/dateUtils'
//   '../data/challengeLibrary' → '@/data/challengeLibrary'
//   '../lib/storeKeys'         → '@/lib/storeKeys'
//   './useSyncStore'           UNCHANGED — both files are in hooks/sync/
//   '@/contexts/ProfileContext' UNCHANGED — @/ alias, always safe
//
// ── PREVIOUS PATCHES (retained) ──────────────────────────────────────────────
//   Centralised auto challenge check-in hook. Any page can call autoCheckIn()
//   after an activity completes to advance matching challenge streaks.
//   useAutoCheckInEffect() — drop-in for Dashboard's habit auto check-in.
//
// REMOVED: useAutoStartLevelChallenges — milestone/level system removed.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef } from 'react';
import { useSyncStore } from './useSyncStore';
import { formatDate } from '@/utils/helpers';
import { resolveAutoProgress } from '@/data/challengeLibrary';
import {
  STORE_CHALLENGES,
} from '@/lib/storeKeys';

// ── Habit → challenge category/keyword mapping ────────────────────────────────
const HABIT_TO_CATEGORIES = {
  habit_bible:     ['bible', 'reading', 'devotion', 'scripture', 'word'],
  habit_journal:   ['journal', 'writing', 'devotion', 'reflection', 'growth'],
  habit_pray:      ['prayer', 'intercession', 'pray', 'devotion'],
  habit_contact:   ['evangelism', 'contact', 'outreach', 'gospel'],
  habit_expense:   ['stewardship', 'budget', 'finance', 'tithe', 'offering'],
  habit_sermon:    ['sermon', 'notes', 'study', 'message', 'teaching'],
  habit_disciple:  ['discipleship', 'meeting', 'disciple', 'mentor', 'timothy'],
  prayer:          ['prayer', 'intercession', 'pray', 'devotion'],
  word:            ['bible', 'reading', 'devotion', 'scripture', 'word'],
  evangelism:      ['evangelism', 'contact', 'outreach', 'mission', 'gospel'],
  mission:         ['mission', 'evangelism', 'outreach', 'contact', 'gospel'],
  discipleship:    ['discipleship', 'meeting', 'disciple', 'mentor', 'timothy'],
  growth:          ['growth', 'journal', 'devotion', 'reflection'],
  stewardship:     ['stewardship', 'budget', 'finance', 'tithe', 'offering'],
};

function getCategories(habitIdOrLabel) {
  const direct = HABIT_TO_CATEGORIES[habitIdOrLabel];
  if (direct) return direct;
  const tokens = habitIdOrLabel.toLowerCase().split(/[\s_\-/]/);
  const found  = [];
  for (const [, cats] of Object.entries(HABIT_TO_CATEGORIES)) {
    if (cats.some(c => tokens.some(t => c.includes(t) || t.includes(c)))) {
      found.push(...cats);
    }
  }
  return [...new Set(found)];
}

// Challenges that need a real-world action the app cannot observe. Keyword matching would
// credit them from unrelated activity (e.g. any Bible reading), so they stay manual check-in.
const MANUAL_ONLY_TEMPLATES = new Set(['memory_verse']);

function todayLocal() {
  return formatDate(new Date());
}

// ── Main hook ─────────────────────────────────────────────────────────────────
export function useAutoCheckIn() {
  const [challenges, setChallenges] = useSyncStore(STORE_CHALLENGES, []);

  const autoCheckIn = useCallback((habitIdOrLabel) => {
    if (!habitIdOrLabel) return;
    const today      = todayLocal();
    const categories = getCategories(habitIdOrLabel);
    if (categories.length === 0) return;

    setChallenges(prev => {
      const arr     = Array.isArray(prev) ? prev : [];
      let   changed = false;

      const next = arr.map(c => {
        const daysDone = (c.checkIns ?? []).length;
        if (daysDone >= (c.days ?? 0)) return c;
        if ((c.checkIns ?? []).includes(today)) return c;
        // Real-data tracked challenges are credited only by useAutoChallengeProgress;
        // keyword matching would credit them for unrelated activity.
        if (MANUAL_ONLY_TEMPLATES.has(c.templateId)) return c;
        if (resolveAutoProgress(c)) return c;

        const cLabel    = (c.label    ?? '').toLowerCase();
        const cCategory = (c.category ?? '').toLowerCase();

        const matches = categories.some(cat =>
          cCategory.includes(cat) ||
          cLabel.includes(cat) ||
          (cCategory.length > 0 && cat.includes(cCategory)) ||
          (cLabel.length > 0    && cat.includes(cLabel))
        );

        if (!matches) return c;
        changed = true;
        return { ...c, checkIns: [...(c.checkIns ?? []), today] };
      });

      return changed ? next : prev;
    });
  }, [setChallenges]);

  return { autoCheckIn };
}

// ── Drop-in for Dashboard's habitStates auto check-in effect ─────────────────
export function useAutoCheckInEffect(habitStates, autoCheckInProp) {
  const ownInstance = useAutoCheckIn();
  const autoCheckIn = autoCheckInProp ?? ownInstance.autoCheckIn;
  const prevStatesRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(habitStates)) return;

    if (prevStatesRef.current === null) {
      // First run — initialise without firing (avoids false transitions on mount)
      prevStatesRef.current = habitStates.map(h => ({ id: h.id, checked: h.checked }));
      return;
    }

    const prevMap = new Map(prevStatesRef.current.map(h => [h.id, h.checked]));

    for (const habit of habitStates) {
      const wasChecked = prevMap.get(habit.id) ?? false;
      if (!wasChecked && habit.checked) {
        autoCheckIn(habit.id);
      }
    }

    prevStatesRef.current = habitStates.map(h => ({ id: h.id, checked: h.checked }));
  }, [habitStates, autoCheckIn]);
}

// ── Standalone helper (no hook) ───────────────────────────────────────────────
export function checkInChallenges(challengesArr, habitIdOrLabel) {
  const arr        = Array.isArray(challengesArr) ? challengesArr : [];
  const today      = todayLocal();
  const categories = getCategories(habitIdOrLabel);
  if (categories.length === 0) return arr;

  let changed = false;
  const next = arr.map(c => {
    const daysDone = (c.checkIns ?? []).length;
    if (daysDone >= (c.days ?? 0)) return c;
    if ((c.checkIns ?? []).includes(today)) return c;
    // Real-data tracked challenges are credited only by useAutoChallengeProgress.
    if (MANUAL_ONLY_TEMPLATES.has(c.templateId)) return c;
    if (resolveAutoProgress(c)) return c;

    const cLabel    = (c.label    ?? '').toLowerCase();
    const cCategory = (c.category ?? '').toLowerCase();

    const matches = categories.some(cat =>
      cCategory.includes(cat) ||
      cLabel.includes(cat)    ||
      (cCategory.length > 0 && cat.includes(cCategory)) ||
      (cLabel.length > 0    && cat.includes(cLabel))
    );

    if (!matches) return c;
    changed = true;
    return { ...c, checkIns: [...(c.checkIns ?? []), today] };
  });

  return changed ? next : arr;
}